/** Dedicated Entra/MSAL redirect path. Must be registered as an SPA redirect URI. */
export const AUTH_REDIRECT_PATH = '/auth/redirect';

/** After Sign out. Must be registered as an SPA redirect URI (post-logout). */
export const POST_LOGOUT_PATH = '/logout';

const REDIRECT_START_PAGE_KEY = 'ntforms:authRedirectStartPage';

function trimTrailingSlash(value: string): string {
  return value.replace(/\/$/, '');
}

/**
 * Login callback URL. Appends /auth/redirect to the configured origin when needed
 * so an env value of https://forms.net-cloud.com still lands on the dedicated page.
 */
export function getAuthRedirectUri(): string {
  const configured = (import.meta.env.VITE_AZURE_REDIRECT_URI as string | undefined)?.trim();
  const base = trimTrailingSlash(configured || window.location.origin);
  return base.endsWith(AUTH_REDIRECT_PATH) ? base : `${base}${AUTH_REDIRECT_PATH}`;
}

export function getPostLogoutRedirectUri(): string {
  return `${window.location.origin}${POST_LOGOUT_PATH}`;
}

export function clearRedirectStartPage(): void {
  sessionStorage.removeItem(REDIRECT_START_PAGE_KEY);
}

export function isAuthRedirectLocation(pathname: string = window.location.pathname): boolean {
  const normalized = trimTrailingSlash(pathname) || '/';
  return normalized === AUTH_REDIRECT_PATH;
}

function isSafeInternalPath(path: string): boolean {
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) {
    return false;
  }

  try {
    const url = new URL(path, window.location.origin);
    return url.origin === window.location.origin;
  } catch {
    return false;
  }
}

function shouldRememberPath(path: string): boolean {
  const pathname = path.split('?')[0] ?? path;
  if (isAuthRedirectLocation(pathname)) {
    return false;
  }

  if (
    pathname === '/login'
    || pathname.startsWith('/login/')
    || pathname === POST_LOGOUT_PATH
    || pathname === '/unauthorized'
  ) {
    return false;
  }

  return isSafeInternalPath(path);
}

/** Remember the in-app page to restore after Entra returns to /auth/redirect. */
export function rememberRedirectStartPage(href?: string): void {
  const url = new URL(href ?? window.location.href, window.location.origin);
  if (url.origin !== window.location.origin) {
    return;
  }

  const path = `${url.pathname}${url.search}`;
  if (!shouldRememberPath(path)) {
    return;
  }

  sessionStorage.setItem(REDIRECT_START_PAGE_KEY, path);
}

export function consumeRedirectStartPage(): string {
  const stored = sessionStorage.getItem(REDIRECT_START_PAGE_KEY);
  sessionStorage.removeItem(REDIRECT_START_PAGE_KEY);

  if (!stored || !shouldRememberPath(stored)) {
    return '/';
  }

  return stored;
}
